import {
  AddToShoppingCartContext,
  AddToShoppingCartSource,
  ShoppingCartProductType,
} from '@monofront/types';
import { format, getHasHiddenPrice } from '@monofront/business/utils';
import { memo } from 'react';

import {
  HorizontalProductAddToCart,
  HorizontalProductBadges,
  HorizontalProductCardSlim,
  HorizontalProductImage,
  HorizontalProductPrice,
  HorizontalProductTitle,
  ProductBundle,
  ProductFormatAndUnitPrice,
  ProductPfand,
  ProductPromotionalOffer,
  ProductVendor,
  SideCartHorizontalProductCard,
} from '@monofront/design-system/components/ProductCard';
import useProductToShoppingCart from '@hooks/cart/useProductToShoppingCart';
import { useMediaQuery } from '@monofront/business/hooks';
import ButtonAddToWishlist from '@components/ButtonAddToWishlist';
import { HorizontalHiddenPrice } from '@monofront/design-system/components/ProductCard/components/ProductPrice';
import { useAuthentication } from '@context/businessContainer';
import { ProductTotalPriceWithPfand } from '@monofront/design-system/components/ProductCard/components/ProductTotalPrice';
import { CartHorizontalProductAddToCart } from '@monofront/design-system/components/ProductCard/components/ProductAddToCart';

interface IShoppingCartProductRowProps {
  product: ShoppingCartProductType;
  containerContext?: AddToShoppingCartContext;
}

const ShoppingCartProductRow: React.FC<IShoppingCartProductRowProps> = ({
  product,
  containerContext,
}) => {
  const { localProductQuantity, incrementCount, decrementCount } =
    useProductToShoppingCart(
      product,
      containerContext ?? { source: AddToShoppingCartSource.DEFAULT },
    );

  const isMobile = useMediaQuery(680);
  const isWideMobile = useMediaQuery(320);

  const { isAuthenticated } = useAuthentication();
  const shouldHidePrice = Boolean(
    !isAuthenticated && getHasHiddenPrice(product),
  );

  const SelectedProductCard = isMobile
    ? SideCartHorizontalProductCard
    : HorizontalProductCardSlim;

  const hasPfand = product.meta.finance?.pfand;

  return (
    <>
      <SelectedProductCard key={product.id} product={product}>
        {!isWideMobile && <HorizontalProductImage />}
        <HorizontalProductBadges />
        <HorizontalProductTitle />
        <ButtonAddToWishlist product={product} />
        <ProductVendor />
        <ProductFormatAndUnitPrice quantity={localProductQuantity ?? 0} />
        <ProductBundle />
        <ProductPromotionalOffer quantity={localProductQuantity ?? 0} />
        {!shouldHidePrice && (
          <HorizontalProductPrice quantity={localProductQuantity ?? 0} />
        )}
        {shouldHidePrice && <HorizontalHiddenPrice />}
        {hasPfand && isMobile && <ProductPfand />}
        {isMobile ? (
          <HorizontalProductAddToCart
            handleAddToCart={incrementCount}
            handleRemoveFromCart={decrementCount}
            quantity={localProductQuantity ?? 0}
          />
        ) : (
          <CartHorizontalProductAddToCart
            handleAddToCart={incrementCount}
            handleRemoveFromCart={decrementCount}
            quantity={localProductQuantity ?? 0}
          />
        )}
        {!isMobile && (
          <ProductTotalPriceWithPfand
            totalPrice={format(product.membershipPriceSubtotal ?? 0)}
          />
        )}
      </SelectedProductCard>
    </>
  );
};

/**
 * Prevent re-render of all products in the shopping cart when it is updated
 */
const isEqual = (
  prevProps: IShoppingCartProductRowProps,
  nextProps: IShoppingCartProductRowProps,
): boolean => {
  const { product: prevProduct } = prevProps;
  const { product: nextProduct } = nextProps;

  return JSON.stringify(prevProduct) === JSON.stringify(nextProduct);
};

export default memo(ShoppingCartProductRow, isEqual);
